/**
 * AdminDashboardPage.jsx — /admin
 * Internal, staff-only dashboard (is_staff on the Django User model).
 * Backed by apps/subscriptions/admin_views.py:
 *   GET /api/subscriptions/admin/overview/
 *   GET /api/subscriptions/admin/users/?search=&status=&page=
 *   GET /api/subscriptions/admin/alerts/
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import UserDetailModal from './UserDetailModal';

const T = {
  bg:'#07080c', bg2:'#0c0f18', bg3:'#111520', surf:'#161c2a',
  border:'rgba(255,255,255,.07)', borderH:'rgba(255,255,255,.14)',
  white:'#dce6fa', muted:'rgba(220,230,250,.45)', dim:'rgba(220,230,250,.25)',
  cyan:'#00c8f0', purple:'#b78cff', green:'#2ecc71', red:'#e74c3c', yellow:'#f1c40f',
  sans:'"Syne","DM Sans",system-ui,sans-serif',
};

const STATUS_META = {
  paid:      { label:'Paid',      color:T.green  },
  pending:   { label:'Pending',   color:T.yellow },
  failed:    { label:'Failed',    color:T.red    },
  past_due:  { label:'Past Due',  color:T.red    },
  trialing:  { label:'Trialing',  color:T.cyan   },
  cancelled: { label:'Cancelled', color:T.dim    },
  free:      { label:'Free',      color:T.dim    },
};

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-CA', { year:'numeric', month:'short', day:'numeric' });
}

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || { label: status || '—', color: T.dim };
  return (
    <span style={{
      padding:'2px 9px', borderRadius:20, fontSize:11, fontWeight:700,
      background:`${meta.color}18`, border:`1px solid ${meta.color}40`, color:meta.color,
      whiteSpace:'nowrap',
    }}>{meta.label}</span>
  );
}

function MetricCard({ label, value, sub, color = T.cyan }) {
  return (
    <div style={{
      flex:'1 1 180px', minWidth:170, padding:'16px 18px', borderRadius:12,
      background:T.bg2, border:`1px solid ${T.border}`,
    }}>
      <div style={{ fontSize:10.5, fontWeight:700, textTransform:'uppercase', letterSpacing:'.08em', color:T.dim, marginBottom:8 }}>
        {label}
      </div>
      <div style={{ fontSize:24, fontWeight:800, color, fontFamily:T.sans, letterSpacing:'-.01em' }}>
        {value}
      </div>
      {sub && <div style={{ fontSize:11.5, color:T.muted, marginTop:4 }}>{sub}</div>}
    </div>
  );
}

export default function AdminDashboardPage() {
  const { apiFetch, user } = useAuth();

  const [overview,    setOverview]    = useState(null);
  const [alerts,      setAlerts]      = useState([]);
  const [users,       setUsers]       = useState([]);
  const [userCount,   setUserCount]   = useState(0);
  const [numPages,    setNumPages]    = useState(1);
  const [page,        setPage]        = useState(1);
  const [search,      setSearch]      = useState('');
  const [statusFilter,setStatusFilter]= useState('');
  const [loading,     setLoading]     = useState(true);
  const [usersLoading,setUsersLoading]= useState(false);
  const [error,       setError]       = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);

  const loadOverviewAndAlerts = useCallback(async () => {
    setError('');
    try {
      const [ovRes, alRes] = await Promise.all([
        apiFetch('/api/subscriptions/admin/overview/'),
        apiFetch('/api/subscriptions/admin/alerts/'),
      ]);
      if (ovRes?.ok) setOverview(await ovRes.json());
      else if (ovRes) setError('Failed to load overview — is this account marked staff?');
      if (alRes?.ok) setAlerts((await alRes.json()).results || []);
    } catch (e) {
      setError('Failed to load dashboard.');
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  const loadUsers = useCallback(async (opts = {}) => {
    setUsersLoading(true);
    const p = opts.page ?? page;
    const s = opts.search ?? search;
    const st = opts.status ?? statusFilter;
    const params = new URLSearchParams({ page: p, page_size: 25 });
    if (s) params.set('search', s);
    if (st) params.set('status', st);
    try {
      const res = await apiFetch(`/api/subscriptions/admin/users/?${params}`);
      if (res?.ok) {
        const data = await res.json();
        setUsers(data.results || []);
        setUserCount(data.count || 0);
        setNumPages(data.num_pages || 1);
      }
    } catch (e) {
      // swallow — error banner already shown by overview load if this is a permissions issue
    } finally {
      setUsersLoading(false);
    }
  }, [apiFetch, page, search, statusFilter]);

  useEffect(() => { loadOverviewAndAlerts(); }, [loadOverviewAndAlerts]);
  useEffect(() => { loadUsers({ page: 1 }); setPage(1); /* eslint-disable-line */ }, [statusFilter]);
  useEffect(() => { loadUsers(); /* eslint-disable-line */ }, [page]);

  const onSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    loadUsers({ page: 1 });
  };

  if (loading) {
    return (
      <div style={{ minHeight:'100vh', background:T.bg, display:'flex', alignItems:'center', justifyContent:'center' }}>
        <div style={{ width:22, height:22, borderRadius:'50%', border:'2px solid rgba(255,255,255,.1)', borderTopColor:T.cyan, animation:'admin-spin .65s linear infinite' }}/>
        <style>{`@keyframes admin-spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  return (
    <div style={{ background:T.bg, minHeight:'100vh', fontFamily:'"DM Sans",system-ui,sans-serif', padding:'0 0 60px' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800;900&family=DM+Sans:wght@400;500;600&display=swap');
        .admin-table input, .admin-table select { font-family:"DM Sans",system-ui,sans-serif; }
      `}</style>

      {/* Header */}
      <div style={{
        background:T.bg2, borderBottom:`1px solid ${T.border}`,
        padding:'14px 24px', display:'flex', alignItems:'center', justifyContent:'space-between',
        position:'sticky', top:0, zIndex:10,
      }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <Link to="/" style={{ fontSize:16, fontWeight:900, color:T.white, textDecoration:'none', letterSpacing:'-.02em', fontFamily:T.sans }}>
            Tylent<span style={{ color:T.cyan }}>ed</span>
          </Link>
          <span style={{ color:T.dim }}>›</span>
          <span style={{ fontSize:14, fontWeight:700, color:T.white, fontFamily:T.sans }}>🛠️ Admin Dashboard</span>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <span style={{ fontSize:12, color:T.muted }}>{user?.email}</span>
          <Link to="/dashboard" style={{
            padding:'6px 14px', borderRadius:7, border:`1px solid ${T.border}`,
            background:T.surf, color:T.white, fontSize:12.5, textDecoration:'none',
          }}>My Dashboard</Link>
        </div>
      </div>

      <div style={{ maxWidth:1200, margin:'0 auto', padding:'24px' }}>

        {error && (
          <div style={{
            padding:'11px 14px', borderRadius:9, marginBottom:18,
            background:'rgba(231,76,60,.1)', border:'1px solid rgba(231,76,60,.3)',
            color:T.red, fontSize:13,
          }}>{error}</div>
        )}

        {overview && (
          <>
            <div style={{ fontSize:11, color:T.dim, marginBottom:10, textTransform:'uppercase', letterSpacing:'.08em', fontWeight:700 }}>
              {overview.month_label}
            </div>

            {/* Metric cards */}
            <div style={{ display:'flex', flexWrap:'wrap', gap:12, marginBottom:28 }}>
              <MetricCard label="MRR" value={overview.mrr_display} sub={`${overview.active_subscribers} active subscribers`} color={T.cyan}/>
              <MetricCard label="Collected this month" value={overview.collected_this_month_display} color={T.green}/>
              <MetricCard label="Failed $ at risk" value={overview.failed_amount_display} sub={`${overview.failed_payments_this_month} failed payment(s)`} color={T.red}/>
              <MetricCard label="New subscribers" value={overview.new_subscribers_this_month} color={T.purple}/>
              <MetricCard label="Cancelled this month" value={overview.cancelled_this_month} color={T.yellow}/>
              <MetricCard label="Past due" value={overview.past_due_count} color={T.red}/>
              <MetricCard label="Total users" value={overview.total_users} color={T.white}/>
            </div>
          </>
        )}

        {/* Alerts */}
        <div style={{ marginBottom:28 }}>
          <div style={{ fontSize:14, fontWeight:700, color:T.white, marginBottom:10, fontFamily:T.sans }}>
            ⚠️ Needs follow-up {alerts.length > 0 && <span style={{ color:T.red }}>({alerts.length})</span>}
          </div>
          <div style={{ background:T.bg2, border:`1px solid ${T.border}`, borderRadius:10, overflow:'hidden' }}>
            {alerts.length === 0 ? (
              <div style={{ padding:'20px', textAlign:'center', color:T.muted, fontSize:12.5 }}>
                Nothing needs attention right now.
              </div>
            ) : (
              alerts.map((a, i) => (
                <div key={a.user_id + a.reason + i} style={{
                  display:'flex', alignItems:'center', justifyContent:'space-between',
                  padding:'10px 14px', borderBottom: i < alerts.length - 1 ? `1px solid ${T.border}` : 'none',
                  gap:12, flexWrap:'wrap',
                }}>
                  <div style={{ minWidth:0, flex:1 }}>
                    <div style={{ fontSize:13, color:T.white, fontWeight:600 }}>{a.email}</div>
                    <div style={{ fontSize:11.5, color:T.muted, marginTop:2 }}>{a.detail}</div>
                  </div>
                  <div style={{ fontSize:12, color:T.muted, whiteSpace:'nowrap' }}>{a.plan_name}</div>
                  <div style={{ fontSize:12, color:T.red, fontWeight:700, whiteSpace:'nowrap' }}>{a.amount_display}</div>
                  <StatusBadge status={a.reason === 'past_due' ? 'past_due' : 'failed'}/>
                  <div style={{ fontSize:11, color:T.dim, whiteSpace:'nowrap', minWidth:80, textAlign:'right' }}>{fmtDate(a.date)}</div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* User table */}
        <div>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10, flexWrap:'wrap', gap:10 }}>
            <div style={{ fontSize:14, fontWeight:700, color:T.white, fontFamily:T.sans }}>
              👥 Users {userCount > 0 && <span style={{ color:T.dim, fontWeight:400 }}>({userCount})</span>}
              <span style={{ color:T.dim, fontWeight:400, fontSize:11.5, marginLeft:8 }}>click a row to view/edit</span>
            </div>
            <div style={{ display:'flex', gap:8 }} className="admin-table">
              <form onSubmit={onSearchSubmit} style={{ display:'flex', gap:8 }}>
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search email or name…"
                  style={{
                    padding:'7px 11px', borderRadius:7, border:`1px solid ${T.border}`,
                    background:T.bg2, color:T.white, fontSize:12.5, outline:'none', width:200,
                  }}
                />
                <button type="submit" style={{
                  padding:'7px 14px', borderRadius:7, border:`1px solid ${T.border}`,
                  background:T.surf, color:T.white, fontSize:12.5, cursor:'pointer',
                }}>Search</button>
              </form>
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                style={{
                  padding:'7px 11px', borderRadius:7, border:`1px solid ${T.border}`,
                  background:T.bg2, color:T.white, fontSize:12.5, outline:'none', cursor:'pointer',
                }}
              >
                <option value="">All statuses</option>
                {Object.entries(STATUS_META).map(([key, meta]) => (
                  <option key={key} value={key}>{meta.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ background:T.bg2, border:`1px solid ${T.border}`, borderRadius:10, overflow:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12.5 }}>
              <thead>
                <tr style={{ borderBottom:`1px solid ${T.border}`, textAlign:'left' }}>
                  {['User', 'Plan', 'Subscription', 'This Month', 'Renews', 'Joined'].map(h => (
                    <th key={h} style={{ padding:'10px 14px', color:T.dim, fontWeight:700, fontSize:10.5, textTransform:'uppercase', letterSpacing:'.06em', whiteSpace:'nowrap' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {usersLoading ? (
                  <tr><td colSpan={6} style={{ padding:'24px', textAlign:'center', color:T.muted }}>Loading…</td></tr>
                ) : users.length === 0 ? (
                  <tr><td colSpan={6} style={{ padding:'24px', textAlign:'center', color:T.muted }}>No users match.</td></tr>
                ) : (
                  users.map(u => (
                    <tr
                      key={u.user_id}
                      onClick={() => setSelectedUserId(u.user_id)}
                      style={{ borderBottom:`1px solid ${T.border}`, cursor:'pointer' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,.02)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <td style={{ padding:'10px 14px' }}>
                        <div style={{ color:T.white, fontWeight:600 }}>{u.name || '—'}</div>
                        <div style={{ color:T.muted, fontSize:11.5 }}>{u.email}</div>
                      </td>
                      <td style={{ padding:'10px 14px', color:T.muted, whiteSpace:'nowrap' }}>
                        {u.plan_name}
                        {u.price_display !== 'Free' && <span style={{ color:T.dim }}> · {u.price_display}/{u.interval === 'yearly' ? 'yr' : 'mo'}</span>}
                      </td>
                      <td style={{ padding:'10px 14px' }}><StatusBadge status={u.subscription_status === 'active' ? 'paid' : u.subscription_status}/></td>
                      <td style={{ padding:'10px 14px' }}><StatusBadge status={u.this_month_status}/></td>
                      <td style={{ padding:'10px 14px', color:T.muted, whiteSpace:'nowrap' }}>
                        {u.cancel_at_period_end ? <span style={{ color:T.yellow }}>Cancels {fmtDate(u.current_period_end)}</span> : fmtDate(u.current_period_end)}
                      </td>
                      <td style={{ padding:'10px 14px', color:T.dim, whiteSpace:'nowrap' }}>{fmtDate(u.date_joined)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {numPages > 1 && (
            <div style={{ display:'flex', justifyContent:'center', gap:8, marginTop:14 }}>
              <button
                disabled={page <= 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}
                style={{
                  padding:'6px 12px', borderRadius:7, border:`1px solid ${T.border}`,
                  background:T.surf, color: page <= 1 ? T.dim : T.white, fontSize:12,
                  cursor: page <= 1 ? 'default' : 'pointer',
                }}
              >← Prev</button>
              <span style={{ padding:'6px 10px', color:T.muted, fontSize:12 }}>Page {page} of {numPages}</span>
              <button
                disabled={page >= numPages}
                onClick={() => setPage(p => Math.min(numPages, p + 1))}
                style={{
                  padding:'6px 12px', borderRadius:7, border:`1px solid ${T.border}`,
                  background:T.surf, color: page >= numPages ? T.dim : T.white, fontSize:12,
                  cursor: page >= numPages ? 'default' : 'pointer',
                }}
              >Next →</button>
            </div>
          )}
        </div>
      </div>

      {selectedUserId && (
        <UserDetailModal
          userId={selectedUserId}
          onClose={() => setSelectedUserId(null)}
          onSaved={() => { loadUsers(); loadOverviewAndAlerts(); }}
        />
      )}
    </div>
  );
}
